import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { BodySecondary, Caption } from '@liveauctioneers/hammer-ui-core/text';
import * as React from 'react';
import styled, { css } from 'styled-components';

import FormattedCurrency from '@/components/Formatted/FormattedCurrency';
import { useAppSelector } from '@/redux/hooks';
import {
    getItemSummaryCurrency,
    getItemSummaryHighBidEstimate,
    getItemSummaryLowBidEstimate,
    getItemSummarySellerId,
    getItemSummarySellerName,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getSellerUrl } from '@/utils/urls';

import ItemCardLiveButton from './components/ItemCardLiveButton';

type DetailsRowProps = {
    allowBidding?: boolean;
    hideLiveButton?: boolean;
    hideSeller?: boolean;
    isCatalogPageCard?: boolean;
    itemId: number;
    showDate?: boolean;
    showEstimate?: boolean;
};

const DetailsRow = ({
    allowBidding,
    hideLiveButton,
    hideSeller,
    isCatalogPageCard,
    itemId,
    showDate,
    showEstimate,
}: DetailsRowProps) => {
    const currency = useAppSelector((state) => getItemSummaryCurrency(state, itemId));
    const highBidEstimate = useAppSelector((state) => getItemSummaryHighBidEstimate(state, itemId));
    const lowBidEstimate = useAppSelector((state) => getItemSummaryLowBidEstimate(state, itemId));
    const sellerId = useAppSelector((state) => getItemSummarySellerId(state, itemId));
    const sellerName = useAppSelector((state) => getItemSummarySellerName(state, itemId));

    const estimateRange = {
        high: (
            <FormattedCurrency
                key="details-row-high-estimate"
                noFraction
                sourceCurrency={currency}
                value={highBidEstimate}
            />
        ),
        low: (
            <FormattedCurrency
                key="details-row-low-estimate"
                noFraction
                sourceCurrency={currency}
                value={lowBidEstimate}
            />
        ),
    };

    const shouldShowLiveButton = !hideLiveButton && !isCatalogPageCard;

    return (
        <ContainerDiv>
            {showEstimate && (
                <EstimateContainer color="secondary">
                    <EstimateNameContainerSpan>
                        <FormattedMessage
                            id="short_estimate_values"
                            values={estimateRange}
                        />
                    </EstimateNameContainerSpan>
                </EstimateContainer>
            )}
            {!hideSeller && (
                <AuctioneerLinkContainerDiv color="secondary">
                    <AuctioneerNameContainerSpan>
                        {sellerName ? (
                            <AuctioneerLink
                                primaryWeak
                                route
                                to={getSellerUrl(sellerId, sellerName)}
                            >
                                {sellerName}
                            </AuctioneerLink>
                        ) : (
                            <AuctioneerLinkSpan>Jasper52</AuctioneerLinkSpan>
                        )}
                    </AuctioneerNameContainerSpan>
                </AuctioneerLinkContainerDiv>
            )}
            {shouldShowLiveButton && (
                <ItemCardLiveButton
                    allowBidding={allowBidding}
                    itemId={itemId}
                    showDate={showDate}
                />
            )}
        </ContainerDiv>
    );
};

export default React.memo<DetailsRowProps>(DetailsRow);

const ContainerDiv = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    @media (max-width: 440px) {
        flex-direction: column;
    }
`;

const LinkContainer = css`
    flex: 1 1 60%;
    display: flex;
    margin: 0 8px 0 0;
    max-width: 60%;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidthNum + 1}px) {
        margin: 0 12px 0 0;
    }

    @media (max-width: 440px) {
        max-width: 100%;
    }
`;

const EstimateContainer = styled(BodySecondary)`
    ${LinkContainer}
`;

const AuctioneerLinkContainerDiv = styled(Caption)`
    ${LinkContainer}
`;

const NameContainer = css`
    flex-grow: 3;
    white-space: nowrap;
    overflow: hidden;
    display: flex;
`;

const EstimateNameContainerSpan = styled.span`
    ${NameContainer}
    color: ${({ theme }) => theme.colors.grey200};
    display: inline;
    overflow: visible;
    > div {
        margin: 0 5px;
    }
`;

const AuctioneerNameContainerSpan = styled.span`
    ${NameContainer}
`;

const AuctioneerLinkStyles = css`
    flex-grow: 3;
    max-width: 100%;
`;

const AuctioneerLink = styled(Link)`
    ${AuctioneerLinkStyles}
`;

const AuctioneerLinkSpan = styled.span`
    ${AuctioneerLinkStyles}
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    display: block;
    width: 100%;
`;
